import { Button, Card, Input, InputNumber, Select, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { SEQUENCE_ACTION_LABELS, type SequenceAction, type SequenceActionType } from '@crm/shared';
import { useLists, useTags } from '../../features/audience/api';
import { useFields } from '../../features/fields/api';
import { useUsers } from '../../features/users/api';

interface Props {
  value: SequenceAction[];
  onChange: (v: SequenceAction[]) => void;
  /** which action types may be added (all by default) */
  types?: SequenceActionType[];
  addLabel?: string;
}

const blank = (type: SequenceActionType): SequenceAction => {
  switch (type) {
    case 'add_tag':
    case 'remove_tag':
      return { type, tagId: '' };
    case 'add_to_list':
    case 'remove_from_list':
      return { type, listId: '' };
    case 'set_lead_status':
    case 'set_lifecycle_stage':
      return { type, value: '' };
    case 'create_task':
      return { type, title: '', note: null, dueInDays: 0, priority: null, assigneeUserId: null };
  }
};

/** A list of CRM changes: tags, lists, lead status, lifecycle stage, tasks. */
export function ActionsEditor({ value, onChange, types, addLabel = 'Add action' }: Props) {
  const tags = useTags();
  const lists = useLists();
  const fields = useFields('CONTACT');
  const users = useUsers();
  const optionsOf = (key: string) => (fields.data ?? []).find((f) => f.key === key)?.options?.map((o) => ({ value: o.value, label: o.label })) ?? [];
  const allowed = types ?? (Object.keys(SEQUENCE_ACTION_LABELS) as SequenceActionType[]);
  const set = (i: number, a: SequenceAction) => onChange(value.map((x, j) => (j === i ? a : x)));

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="small">
      {value.map((a, i) => (
        <Card key={i} size="small" styles={{ body: { padding: 12 } }}>
          <Space orientation="vertical" style={{ width: '100%' }} size={8}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Select<SequenceActionType>
                aria-label="Action"
                value={a.type}
                style={{ width: 200 }}
                onChange={(t) => set(i, blank(t))}
                options={allowed.map((t) => ({ value: t, label: SEQUENCE_ACTION_LABELS[t] }))}
              />
              <Button type="text" danger icon={<DeleteOutlined />} aria-label="Remove action" onClick={() => onChange(value.filter((_, j) => j !== i))} />
            </Space>
            {(a.type === 'add_tag' || a.type === 'remove_tag') && (
              <Select aria-label="Tag" style={{ width: '100%' }} showSearch optionFilterProp="label" placeholder="Tag" loading={tags.isLoading} value={a.tagId || undefined} onChange={(v) => set(i, { ...a, tagId: v })} options={(tags.data ?? []).map((t) => ({ value: t.id, label: t.path }))} />
            )}
            {(a.type === 'add_to_list' || a.type === 'remove_from_list') && (
              <Select aria-label="List" style={{ width: '100%' }} showSearch optionFilterProp="label" placeholder="List" loading={lists.isLoading} value={a.listId || undefined} onChange={(v) => set(i, { ...a, listId: v })} options={(lists.data ?? []).map((l) => ({ value: l.id, label: l.name }))} />
            )}
            {(a.type === 'set_lead_status' || a.type === 'set_lifecycle_stage') && (
              <Select
                aria-label={SEQUENCE_ACTION_LABELS[a.type]}
                style={{ width: '100%' }}
                placeholder="Value"
                value={a.value || undefined}
                onChange={(v) => set(i, { ...a, value: v })}
                options={optionsOf(a.type === 'set_lead_status' ? 'lead_status' : 'lifecycle_stage')}
              />
            )}
            {a.type === 'create_task' && (
              <>
                <Input aria-label="Task title" placeholder="e.g. Call {{first_name}} at {{organization_name}}" value={a.title} onChange={(e) => set(i, { ...a, title: e.target.value })} maxLength={300} />
                <Input.TextArea aria-label="Task note" placeholder="Note (optional)" autoSize={{ minRows: 1, maxRows: 4 }} value={a.note ?? ''} onChange={(e) => set(i, { ...a, note: e.target.value || null })} />
                <Space wrap>
                  <Space size={4}>
                    <Typography.Text type="secondary">Due in</Typography.Text>
                    <InputNumber aria-label="Due in days" min={0} max={365} value={a.dueInDays} onChange={(v) => set(i, { ...a, dueInDays: v ?? 0 })} style={{ width: 70 }} />
                    <Typography.Text type="secondary">days</Typography.Text>
                  </Space>
                  <Select
                    aria-label="Priority"
                    allowClear
                    placeholder="Priority"
                    style={{ width: 120 }}
                    value={a.priority ?? undefined}
                    onChange={(v) => set(i, { ...a, priority: v ?? null })}
                    options={['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => ({ value: p, label: p.charAt(0) + p.slice(1).toLowerCase() }))}
                  />
                  <Select
                    aria-label="Assignee"
                    allowClear
                    placeholder="Assignee (sequence owner)"
                    style={{ width: 220 }}
                    value={a.assigneeUserId ?? undefined}
                    onChange={(v) => set(i, { ...a, assigneeUserId: v ?? null })}
                    options={(users.data ?? []).filter((u) => u.status === 'ACTIVE').map((u) => ({ value: u.id, label: u.name }))}
                  />
                </Space>
              </>
            )}
          </Space>
        </Card>
      ))}
      <Button icon={<PlusOutlined />} onClick={() => onChange([...value, blank(allowed[0])])}>{addLabel}</Button>
    </Space>
  );
}

/** True when every action has what it needs (a tag, a list, a value, a title). */
export const actionsComplete = (actions: SequenceAction[]) =>
  actions.every((a) => ('tagId' in a ? !!a.tagId : 'listId' in a ? !!a.listId : a.type === 'create_task' ? !!a.title.trim() : !!a.value));
